import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, Search, FileText, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const Productos = () => {
  const [productos, setProductos] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({ nombre: '' });
  const [fileData, setFileData] = useState(null);
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');

  useEffect(() => { loadProductos(); }, []);
  useEffect(() => { applyFilters(); }, [productos, searchTerm]);

  const loadProductos = async () => {
    try {
      const res = await axios.get(`${API}/productos`);
      setProductos(res.data);
    } catch {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let f = [...productos];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      f = f.filter(p => p.nombre?.toLowerCase().includes(term));
    }
    setFiltered(f);
  };

  const handleOpenDialog = (producto = null) => {
    if (producto) {
      setEditingId(producto.id);
      setFormData({ nombre: producto.nombre });
      setFileData(null);
    } else {
      setEditingId(null);
      setFormData({ nombre: '' });
      setFileData(null);
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.nombre) { toast.error('El nombre es requerido'); return; }
    if (!editingId && !fileData) { toast.error('La ficha técnica es requerida al crear'); return; }

    try {
      const data = new FormData();
      data.append('nombre', formData.nombre);
      if (fileData) {
        data.append('ficha_tecnica', fileData);
      }

      if (editingId) {
        await axios.put(`${API}/productos/${editingId}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Producto actualizado');
      } else {
        await axios.post(`${API}/productos`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Producto creado');
      }
      setShowDialog(false);
      loadProductos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/productos/${id}`);
      toast.success('Producto eliminado');
      loadProductos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
    setDeleteId(null);
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="text-muted-foreground">Cargando...</div></div>;

  return (
    <div className="space-y-6 bg-background min-h-screen">
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Productos</h1>
              <p className="text-muted-foreground mt-1">{productos.length} productos registrados</p>
            </div>
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="h-4 w-4 mr-2" /> Nuevo Producto
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Card className="p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar por nombre..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
            </div>
          </div>
        </Card>

        {filtered.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No hay productos</p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filtered.map((prod) => (
              <Card key={prod.id} className="p-4">
                <div className="flex flex-col sm:flex-row gap-4 justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2">{prod.nombre}</h3>
                    {prod.ficha_tecnica_url && (
                      <button onClick={() => { setPdfUrl(BACKEND_URL + prod.ficha_tecnica_url); setShowPdfDialog(true); }} className="text-sm text-blue-600 hover:underline flex items-center gap-1 w-fit bg-transparent border-none cursor-pointer">
                        <FileText className="h-4 w-4" />
                        Ver Ficha Técnica
                      </button>
                    )}
                  </div>
                  <div className="flex sm:flex-col gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleOpenDialog(prod)}><Edit className="h-4 w-4" /></Button>
                    <Button variant="outline" size="sm" onClick={() => setDeleteId(prod.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar' : 'Nuevo'} Producto</DialogTitle>
            <DialogDescription>Completá la información del producto y adjunta su ficha técnica.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Nombre del producto *</Label>
              <Input value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Ficha Técnica (PDF) {editingId ? '(Opcional para mantener el actual)' : '*'}</Label>
              <Input type="file" accept="application/pdf" onChange={(e) => setFileData(e.target.files[0])} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleSave}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar producto?</AlertDialogTitle>
            <AlertDialogDescription>El producto será eliminado lógicamente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteId)}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PDF Dialog */}
      <Dialog open={showPdfDialog} onOpenChange={setShowPdfDialog}>
        <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Ficha Técnica</DialogTitle>
          </DialogHeader>
          <div className="flex-1 w-full bg-muted rounded-md overflow-hidden">
            {pdfUrl && (
              <iframe src={pdfUrl} className="w-full h-full" title="Ficha Técnica" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Productos;
